import { useState } from 'react';
import { Pressable, StyleSheet } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import Svg, { Path } from 'react-native-svg';
import * as S from './style';
import THEME from '../../../config/theme';
import { useCustomTheme } from '../../../contexts/useCustomTheme';
import { useAuthUser } from '../../../contexts/useAuthUser';
import { useCategories } from '../../../queries/useCategories';
import { getCatalogGroup } from '../../../utils/accessUtils';
import {
  buildProductCategories,
  getProductCatalogModuleEntries,
} from '../../../utils/productCatalog';

const ProductItem = ({ product, isLocked, variant = 'carousel' }) => {
  const navigation = useNavigation();
  const { customColor } = useCustomTheme();
  const { user } = useAuthUser();
  const { data: allCategories = [] } = useCategories();
  const [hovered, setHovered] = useState(false);

  const onPress = () => {
    const group = getCatalogGroup(product);
    const productCategories = buildProductCategories(allCategories, group, user);
    const entries = getProductCatalogModuleEntries(productCategories);

    // Um único módulo: pula CatalogoProdutos e abre direto no Módulo. O Voltar
    // cai na Main; catalogProductId só serve pro checkout direto da vitrine.
    if (entries.length === 1) {
      const { card, categoryId, orientation } = entries[0];
      const scrollPosition = window?.scrollY || 0;

      navigation.navigate({
        name: 'Modulo',
        params: {
          cardId: card.id || card.cardId,
          categoryId,
          ...(user?.firstPlanPurchase && { firstPlanPurchase: user.firstPlanPurchase }),
          ...(user?.isAdmin && { orientation }),
          scrollPosition,
          catalogProductId: product.id,
        },
        merge: true,
      });
      return;
    }

    navigation.navigate('CatalogoProdutos', {
      title: product.name,
      productId: product.id,
    });
  };

  const isGrid = variant === 'grid';
  const isList = variant === 'list';
  const enableHover = hovered && (variant === 'carousel' || isGrid);
  const ImageComponent = isGrid || isList ? S.GridImage : S.Image;

  // Sem título por baixo da imagem: o nome do produto já vem desenhado na
  // própria arte que o admin sobe (ver CatalogVitrineFields).
  return (
    <Pressable
      onPress={onPress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      style={[
        styles.card,
        isList && styles.cardList,
        isGrid && styles.cardGrid,
        hovered && variant === 'carousel' && styles.cardHovered,
        enableHover && isGrid && styles.cardGridHovered,
      ]}
    >
      <ImageComponent
        source={{ uri: product.catalogImage?.url }}
        imageStyle={{ resizeMode: 'cover' }}
        style={[
          isGrid || isList ? styles.imageFluid : styles.imageBase,
          enableHover && styles.imageHover,
        ]}
      >
        {isLocked && (
          <S.LockedContainer>
            <S.LockBadge>
              <Svg width={20} height={20} viewBox="0 0 16 16" fill="none">
                <Path
                  d="M4 5.014v-.93c0-1.078.417-2.114 1.165-2.881A3.96 3.96 0 018 0a3.96 3.96 0 012.835 1.203A4.127 4.127 0 0112 4.083v.93a2.25 2.25 0 012 2.237v5.5A2.25 2.25 0 0111.75 15h-7.5A2.25 2.25 0 012 12.75v-5.5a2.25 2.25 0 012-2.236zM6.239 2.25A2.46 2.46 0 018 1.5c.657 0 1.29.267 1.761.75.471.483.739 1.142.739 1.833V5h-5v-.917c0-.69.268-1.35.739-1.833zM8 9.25a.75.75 0 00-.75.75v1a.75.75 0 001.5 0v-1A.75.75 0 008 9.25z"
                  fillRule="evenodd"
                  fill={
                    customColor &&
                      customColor.iconsMain &&
                      customColor.iconsMain.iconLock
                      ? customColor.iconsMain.iconLock
                      : THEME.COLORS.ICON
                  }
                />
              </Svg>
            </S.LockBadge>
          </S.LockedContainer>
        )}
      </ImageComponent>
    </Pressable>
  );
};

// Mesmo efeito hover dos módulos (CategoryItem).
const styles = StyleSheet.create({
  card: {
    cursor: 'pointer',
    position: 'relative',
  },
  cardList: {
    width: '100%',
    marginTop: '0.5rem',
  },
  cardGrid: {
    width: '100%',
  },
  cardGridHovered: {
    zIndex: 5,
  },
  imageFluid: {
    margin: 0,
    width: '100%',
    aspectRatio: 16 / 9,
  },
  cardHovered: {
    zIndex: 5,
  },
  imageBase: {
    transformOrigin: 'left center',
    transitionDuration: '160ms',
    transitionProperty: 'transform, box-shadow',
    transitionTimingFunction: 'ease-out',
  },
  imageHover: {
    transform: [{ scale: 1.06 }],
    boxShadow: '0 6px 16px rgba(0,0,0,0.28)',
  },
});

export default ProductItem;
